import Dialog from "@components/dialog/Dialog";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { IRow, IRowAction, TId } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import BindingContext from "@odata/BindingContext";
import { memoizeOne } from "@utils/memoizeOne";
import React from "react";

import { EMPTY_DASH } from "../../constants";
import { ActionState, RowAction, TableSizes, ToggleState } from "../../enums";
import i18n from "../../i18n";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { TSmartVirtualizedFormStorage } from "./VirtualizedForm.utils";

interface IProps {
    storage: TSmartVirtualizedFormStorage;
    bindingContext: BindingContext;
    groupDef: IFormGroupDef;
    rows: string[];
    visibleRows: string[];
    onClose: () => void;
    onConfirm: (selectedRows: string[]) => void;
}

interface IState {
    selectedRows: string[];
}

export default class VirtualizedFormGroupCustomizationDialog extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);

        this.state = {
            selectedRows: props.visibleRows
        };
    }

    customizationColumns = memoizeOne(() => {
        return [
            {
                id: "id",
                label: i18n.t("Common:VirtualizedForm.DialogColumnId"),
            },
            {
                id: "label",
                label: i18n.t("Common:VirtualizedForm.DialogColumnLabel"),
            }
        ];
    });

    handleConfirm = (): void => {
        this.props.onConfirm(this.state.selectedRows);
    };

    handleCustomizationRowSelect = (id: TId): void => {
        const { selectedRows } = this.state;
        const rowId = id as string;
        if (selectedRows.includes(rowId)) {
            this.setState({
                selectedRows: selectedRows.filter(r => r !== rowId)
            });
        } else {
            this.setState({ selectedRows: [...selectedRows, rowId] });
        }
    };



    rowAction = memoizeOne((): IRowAction => {
        let toggleState: ToggleState;
        const { selectedRows } = this.state;

        if (selectedRows.length === 0) {
            toggleState = ToggleState.AllUnchecked;
        } else if (selectedRows.length === this.props.rows.length) {
            toggleState = ToggleState.AllChecked;
        } else {
            toggleState = ToggleState.Other;
        }

        return {
            actionType: RowAction.Check,
            getActionState: (rowId: TId): ActionState =>
                this.state.selectedRows.includes(rowId as string) ? ActionState.Active : ActionState.Inactive,
            onClick: this.handleCustomizationRowSelect,
            onToggleChange: toggleState => {
                if (toggleState === ToggleState.AllChecked) {
                    this.setState({ selectedRows: this.props.rows });
                } else {
                    this.setState({ selectedRows: [] });
                }
            },
            toggleState
        };
    }, () => [this.state.selectedRows]);

    render() {
        const { storage, bindingContext, rows: fieldIds } = this.props;

        const rows: IRow[] = fieldIds.map(fieldId => {
            const bc = bindingContext.navigate(fieldId);
            const def = storage.getInfo(bc);
            return {
                id: fieldId,
                values: {
                    id: def.metadata?.id ?? EMPTY_DASH,
                    label: def.label ?? EMPTY_DASH
                }
            };
        });

        const rowsLength = Math.max(rows.length, 3); // minimal height for 3 rows, when there are less than 3 hidable fields
        const height = (TableSizes.RowHeight + TableSizes.RowSpacing) * (rowsLength + 1) + 150; // rows + header + some padding

        return (
            <Dialog title={i18n.t("Common:VirtualizedForm.DialogTitle")}
                    onConfirm={this.props.onClose}
                    onClose={this.handleConfirm}
                    height={`${height}px`}
                    width={"650px"}
                    footer={<ConfirmationButtons onCancel={this.props.onClose}
                                                 onConfirm={this.handleConfirm}
                                                 useWrapper={false}/>}
            >
                <TableWithAutoSizedColumns columns={this.customizationColumns()}
                                           rows={rows}
                                           rowAction={this.rowAction()}
                                           onRowSelect={this.handleCustomizationRowSelect}
                                           disableColumnResize
                                           disableVerticalScroll
                                           disableSort
                />
            </Dialog>
        );
    }
}